import React, { useEffect, useState, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import './SecurityControlsDetailPage.css';

interface SecurityControl {
  id: string;
  name: string;
  type: 'Process' | 'File' | 'Network';
  status: 'Active' | 'Inactive';
  allowed: number;
  blocked: number;
  effectiveness: number;
  namespace: string;
  lastUpdated: string;
}

const SecurityControlsDetailPage: React.FC = () => {
  const { t } = useLanguage();
  const [selectedControl, setSelectedControl] = useState<SecurityControl | null>(null);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const controlData: SecurityControl[] = useMemo(() => ([
    { id: 'ctrl-1', name: 'Process Execution Whitelist', type: 'Process', status: 'Active', allowed: 1245, blocked: 23, effectiveness: 98, namespace: 'production', lastUpdated: '2024-01-15' },
    { id: 'ctrl-2', name: 'Malicious Process Prevention', type: 'Process', status: 'Active', allowed: 892, blocked: 156, effectiveness: 94, namespace: 'production', lastUpdated: '2024-01-14' },
    { id: 'ctrl-3', name: 'Suspicious Syscall Detection', type: 'Process', status: 'Active', allowed: 3456, blocked: 78, effectiveness: 96, namespace: 'default', lastUpdated: '2024-01-15' },
    { id: 'ctrl-4', name: 'Sensitive File Access Control', type: 'File', status: 'Active', allowed: 2134, blocked: 45, effectiveness: 97, namespace: 'production', lastUpdated: '2024-01-15' },
    { id: 'ctrl-5', name: 'Configuration File Protection', type: 'File', status: 'Active', allowed: 1567, blocked: 12, effectiveness: 99, namespace: 'default', lastUpdated: '2024-01-14' },
    { id: 'ctrl-6', name: 'Database Access Monitoring', type: 'File', status: 'Inactive', allowed: 0, blocked: 0, effectiveness: 0, namespace: 'staging', lastUpdated: '2024-01-10' },
    { id: 'ctrl-7', name: 'Outbound Network Restriction', type: 'Network', status: 'Active', allowed: 4567, blocked: 234, effectiveness: 95, namespace: 'production', lastUpdated: '2024-01-15' },
    { id: 'ctrl-8', name: 'External Communication Blocking', type: 'Network', status: 'Active', allowed: 3421, blocked: 567, effectiveness: 92, namespace: 'default', lastUpdated: '2024-01-14' },
  ]), []);

  const filteredControls = useMemo(() => {
    return controlData.filter(control => {
      const typeMatch = filterType === 'all' || control.type === filterType;
      const statusMatch = filterStatus === 'all' || control.status === filterStatus;
      return typeMatch && statusMatch;
    });
  }, [filterType, filterStatus, controlData]);

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Process': return '#ec4899';
      case 'File': return '#8b5cf6';
      case 'Network': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'Active' ? '#22c55e' : '#9ca3af';
  };

  const getEffectivenessColor = (effectiveness: number) => {
    if (effectiveness >= 95) return '#22c55e';
    if (effectiveness >= 90) return '#84cc16';
    if (effectiveness >= 80) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div className="detail-page security-controls">
      <div className="detail-header">
        <h1>{t('securityControls.title')}</h1>
        <p>보안 제어 규칙 및 효과성 분석</p>
      </div>

      {/* 필터 섹션 */}
      <section className="filter-section">
        <div className="filter-group">
          <label>{t('securityControls.filters.controlType')}</label>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">{t('securityControls.filters.all')}</option>
            <option value="Process">Process</option>
            <option value="File">File</option>
            <option value="Network">Network</option>
          </select>
        </div>
        <div className="filter-group">
          <label>{t('securityControls.filters.status')}</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">{t('securityControls.filters.all')}</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
        <div className="filter-stat">
          <span>{t('securityControls.filters.searchResults')}: <strong>{filteredControls.length}</strong></span>
        </div>
      </section>

      {/* 테이블 및 상세 정보 */}
      <div className="content-layout">
        {/* 데이터 테이블 */}
        <section className="table-section">
          <div className="table-header">
            <div>{t('securityControls.columns.controlName')}</div>
            <div>{t('securityControls.columns.type')}</div>
            <div>{t('securityControls.columns.status')}</div>
            <div>{t('securityControls.columns.allowed')}</div>
            <div>{t('securityControls.columns.blocked')}</div>
            <div>{t('securityControls.columns.effectiveness')}</div>
          </div>
          <div className="table-body">
            {filteredControls.map((control) => (
              <div
                key={control.id}
                className={`table-row ${selectedControl?.id === control.id ? 'active' : ''}`}
                onClick={() => setSelectedControl(control)}
              >
                <div>{control.name}</div>
                <div>
                  <span className="type-badge" style={{backgroundColor: `${getTypeColor(control.type)}20`, color: getTypeColor(control.type)}}>
                    {control.type}
                  </span>
                </div>
                <div>
                  <span className="status-badge" style={{backgroundColor: `${getStatusColor(control.status)}20`, color: getStatusColor(control.status)}}>
                    {control.status}
                  </span>
                </div>
                <div style={{color: '#22c55e', fontWeight: 'bold'}}>{control.allowed}</div>
                <div style={{color: '#ef4444', fontWeight: 'bold'}}>{control.blocked}</div>
                <div>
                  <div className="effectiveness-bar">
                    <div className="effectiveness-fill" style={{width: `${control.effectiveness}%`, backgroundColor: getEffectivenessColor(control.effectiveness)}} />
                  </div>
                  <span style={{fontSize: '11px', color: getEffectivenessColor(control.effectiveness)}}>{control.effectiveness}%</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 상세 정보 */}
        <section className="detail-section">
          {selectedControl ? (
            <div className="detail-card">
              <div className="detail-title">제어 상세</div>
              <div className="detail-content">
                <div className="detail-row">
                  <div className="detail-label">제어 ID</div>
                  <div className="detail-value">{selectedControl.id}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">제어명</div>
                  <div className="detail-value">{selectedControl.name}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">타입</div>
                  <div className="detail-value">
                    <span className="type-badge" style={{backgroundColor: `${getTypeColor(selectedControl.type)}20`, color: getTypeColor(selectedControl.type)}}>
                      {selectedControl.type}
                    </span>
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">상태</div>
                  <div className="detail-value">
                    <span className="status-badge" style={{backgroundColor: `${getStatusColor(selectedControl.status)}20`, color: getStatusColor(selectedControl.status)}}>
                      {selectedControl.status}
                    </span>
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">허용됨</div>
                  <div className="detail-value" style={{color: '#22c55e', fontWeight: 'bold'}}>{selectedControl.allowed}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">차단됨</div>
                  <div className="detail-value" style={{color: '#ef4444', fontWeight: 'bold'}}>{selectedControl.blocked}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">효과성</div>
                  <div className="detail-value">
                    <div className="effectiveness-bar" style={{marginBottom: '4px'}}>
                      <div className="effectiveness-fill" style={{width: `${selectedControl.effectiveness}%`, backgroundColor: getEffectivenessColor(selectedControl.effectiveness)}} />
                    </div>
                    <span style={{color: getEffectivenessColor(selectedControl.effectiveness)}}>{selectedControl.effectiveness}%</span>
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">네임스페이스</div>
                  <div className="detail-value">{selectedControl.namespace}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">마지막 업데이트</div>
                  <div className="detail-value">{selectedControl.lastUpdated}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="detail-card empty-detail">
              <div className="empty-state-message">
                <p>📋 목록에서 제어를 선택하세요.</p>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default SecurityControlsDetailPage;
